import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ApiError,
  clearJwt,
  getJwt,
  getMe,
  listAppTools,
  listApps,
  setSsoSession,
  type App,
} from "../lib/api";
import { MBIcon, MBWordmark } from "./mb";

export function Shell() {
  const nav = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: getMe, enabled: !!getJwt(), retry: false });

  useEffect(() => {
    if (getJwt()) void setSsoSession();
  }, []);

  useEffect(() => {
    if (!getJwt()) {
      nav("/login", { replace: true });
      return;
    }
    if (me.error instanceof ApiError && me.error.status === 401) {
      clearJwt();
      nav("/login", { replace: true });
    }
  }, [me.error, nav]);

  return (
    <div style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <AppNav email={me.data?.email} tier={me.data?.tier} />
      <main style={{ flex: 1, minWidth: 0 }}>
        <Outlet />
      </main>
    </div>
  );
}

function initials(email?: string): string {
  if (!email) return "··";
  const local = email.split("@")[0] ?? email;
  const parts = local.split(/[.\-_]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

function AppNav({ email, tier }: { email?: string; tier?: string }) {
  const nav = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Inbox badge — count of unsaved widgets in the ephemeral Inbox project.
  const inbox = useQuery({
    queryKey: ["app-tools", "__inbox"],
    queryFn: () => listAppTools("__inbox"),
    enabled: !!getJwt(),
    retry: false,
  });
  const inboxCount = (inbox.data ?? []).filter((t) => t.renderable !== false).length;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const tabs = [
    { label: "Projects", to: "/projects", end: true as const },
    { label: "Inbox", to: "/projects/__inbox", badge: inboxCount },
    { label: "Shared", to: "/shared" },
    { label: "Docs", to: "/docs" },
    { label: "Settings", to: "/settings" },
  ];

  return (
    <>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          height: 56,
          borderBottom: "1px solid var(--line-2)",
          background: "rgba(7,9,12,0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <div style={{ maxWidth: 1320, margin: "0 auto", height: "100%", padding: "0 28px", display: "flex", alignItems: "center", gap: 28 }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center" }}>
            <MBWordmark size={17} />
          </Link>
          <nav style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {tabs.map((t) => (
              <NavLink
                key={t.label}
                to={t.to}
                end={"end" in t ? t.end : undefined}
                style={({ isActive }) => ({
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 6,
                  background: isActive ? "var(--surface-2)" : "transparent",
                  color: isActive ? "var(--fg-1)" : "var(--fg-3)",
                  fontFamily: "var(--font-sans)",
                  fontSize: 14,
                  fontWeight: 500,
                  border: isActive ? "1px solid var(--line-2)" : "1px solid transparent",
                  whiteSpace: "nowrap",
                })}
              >
                {t.label}
                {t.badge != null && t.badge > 0 && (
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      color: "var(--accent-fg)",
                      background: "var(--accent-soft)",
                      border: "1px solid var(--accent-line)",
                      padding: "1px 6px",
                      borderRadius: 999,
                      lineHeight: 1.4,
                    }}
                  >
                    {t.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center" }}>
            <button
              onClick={() => setPaletteOpen(true)}
              className="max-sm:hidden"
              style={{
                height: 32,
                padding: "0 10px",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "var(--surface-1)",
                color: "var(--fg-3)",
                border: "1px solid var(--line-2)",
                borderRadius: 6,
                cursor: "pointer",
                fontFamily: "var(--font-mono)",
                fontSize: 11.5,
                letterSpacing: "0.04em",
                minWidth: 200,
                justifyContent: "flex-start",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              <MBIcon name="search" size={13} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>search projects…</span>
              <span style={{ marginLeft: "auto", color: "var(--fg-5)", letterSpacing: 0 }}>⌘K</span>
            </button>
            <button
              onClick={() => nav("/settings")}
              title="Settings"
              style={{
                width: 32,
                height: 32,
                border: "1px solid var(--line-2)",
                background: "var(--surface-1)",
                borderRadius: 6,
                cursor: "pointer",
                color: "var(--fg-3)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MBIcon name="settings" size={15} />
            </button>
            <UserChip email={email} tier={tier} />
          </div>
        </div>
      </header>

      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
    </>
  );
}

function UserChip({ email, tier }: { email?: string; tier?: string }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "4px 4px 4px 12px",
          background: "var(--surface-1)",
          border: "1px solid var(--line-2)",
          borderRadius: 999,
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
      >
        <span className="max-sm:hidden" style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-3)" }}>
          {email ? email.split("@")[0] : "account"}
        </span>
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "linear-gradient(135deg, var(--accent), var(--surface-4))",
            color: "#fff",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {initials(email)}
        </span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            minWidth: 220,
            background: "var(--surface-1)",
            border: "1px solid var(--line-2)",
            borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
            padding: 8,
            zIndex: 60,
          }}
        >
          <div style={{ padding: "8px 10px 10px", borderBottom: "1px solid var(--line-1)", marginBottom: 6 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-2)", wordBreak: "break-all" }}>{email}</div>
            {tier && (
              <div style={{ marginTop: 6, fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: tier === "free" ? "var(--fg-4)" : "var(--accent-fg)" }}>
                {tier} plan
              </div>
            )}
          </div>
          <button
            onMouseDown={() => nav("/settings")}
            style={menuItemStyle}
          >
            Settings
          </button>
          <button
            onMouseDown={() => {
              clearJwt();
              nav("/login", { replace: true });
            }}
            style={{ ...menuItemStyle, color: "var(--fg-3)" }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const menuItemStyle = {
  display: "block",
  width: "100%",
  textAlign: "left" as const,
  padding: "8px 10px",
  background: "transparent",
  border: 0,
  borderRadius: 6,
  color: "var(--fg-1)",
  fontFamily: "var(--font-sans)",
  fontSize: 13.5,
  cursor: "pointer",
};

// Lightweight jump-to-project palette (⌘K). Filters the user's projects and
// offers quick links; navigates on click or Enter.
function CommandPalette({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const apps = useQuery({ queryKey: ["apps"], queryFn: listApps, retry: false });

  const items = useMemo(() => {
    const projects = (apps.data ?? [])
      .filter((a: App) => a.slug !== "__inbox")
      .map((a) => ({ kind: "project" as const, label: a.name, hint: a.slug, to: `/projects/${a.slug}`, icon: a.icon ?? "🧩" }));
    const links = [
      { kind: "link" as const, label: "All projects", hint: "/projects", to: "/projects", icon: "📁" },
      { kind: "link" as const, label: "Inbox", hint: "__inbox", to: "/projects/__inbox", icon: "📥" },
      { kind: "link" as const, label: "Settings", hint: "/settings", to: "/settings", icon: "⚙️" },
    ];
    const all = [...links, ...projects];
    if (!q.trim()) return all;
    const needle = q.toLowerCase();
    return all.filter((i) => i.label.toLowerCase().includes(needle) || i.hint.toLowerCase().includes(needle));
  }, [apps.data, q]);

  const go = (to: string) => {
    onClose();
    nav(to);
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 80, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "12vh" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 540, maxWidth: "92vw", background: "var(--surface-1)", border: "1px solid var(--line-3)", borderRadius: 12, boxShadow: "0 24px 60px rgba(0,0,0,0.6)", overflow: "hidden" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--line-1)" }}>
          <MBIcon name="search" size={15} color="var(--fg-3)" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              if (e.key === "Enter" && items[0]) go(items[0].to);
            }}
            placeholder="Search projects, jump to a page…"
            style={{ flex: 1, background: "transparent", border: 0, outline: "none", color: "var(--fg-1)", fontFamily: "var(--font-sans)", fontSize: 15 }}
          />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--fg-5)", padding: "2px 5px", border: "1px solid var(--line-2)", borderRadius: 3 }}>esc</span>
        </div>
        <div style={{ maxHeight: 360, overflowY: "auto", padding: 6 }}>
          {items.length === 0 && (
            <div style={{ padding: "20px 12px", textAlign: "center", color: "var(--fg-3)", fontFamily: "var(--font-sans)", fontSize: 14 }}>No matches.</div>
          )}
          {items.map((it) => (
            <button
              key={it.kind + it.to}
              onClick={() => go(it.to)}
              style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: "10px 12px", background: "transparent", border: 0, borderRadius: 8, cursor: "pointer", color: "var(--fg-1)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span style={{ fontSize: 16 }}>{it.icon}</span>
              <span style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 500 }}>{it.label}</span>
              <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-4)" }}>{it.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
